import axios from "axios";
import { call, put, takeLatest } from "redux-saga/effects";
import { buildApiUrl, getAuthHeaders } from "../../../api/apiClient";
import {
  createFieldFailure,
  createFieldStart,
  createFieldSuccess,
  deleteFieldFailure,
  deleteFieldStart,
  deleteFieldSuccess,
  fetchFieldDetailFailure,
  fetchFieldDetailStart,
  fetchFieldDetailSuccess,
  updateFieldFailure,
  updateFieldStart,
  updateFieldSuccess,
} from "../../../store/features/customer/field/fieldSlice";
import { fetchFieldsStart } from "../../../store/features/customer/fields/fieldsSlice";

const URL = buildApiUrl("/api/v1/field/field/");

const getApiObject = (payload) => {
  if (payload?.data && !Array.isArray(payload.data)) {
    return payload.data;
  }

  return payload;
};

// GET FIELD
const getFieldDetail = async (accessToken, fieldId) => {
  try {
    const response = await axios.get(`${URL}${fieldId}/`, {
      headers: getAuthHeaders(accessToken),
      // Defeat any intermediate cache (browser, dev proxy, nginx) so that
      // after a save/logout-login we always read the current BE state.
      params: { _: Date.now() },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleGetFieldDetail(action) {
  try {
    const { accessToken, fieldId } = action.payload;
    const fieldDetailData = yield call(getFieldDetail, accessToken, fieldId);
    yield put(fetchFieldDetailSuccess(fieldDetailData));
  } catch (error) {
    yield put(fetchFieldDetailFailure(error.message));
  }
}

// CREATE
const createFieldApi = async ({ data, accessToken }) => {
  try {
    const response = await axios.post(URL, data, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleCreateFieldSaga(action) {
  try {
    const data = yield call(createFieldApi, action.payload);
    const { accessToken } = action.payload;
    const createdField = getApiObject(data);

    if (accessToken && createdField?.id) {
      const fieldDetailData = yield call(
        getFieldDetail,
        accessToken,
        createdField.id
      );
      yield put(createFieldSuccess(fieldDetailData));
      yield put(fetchFieldsStart({ accessToken }));
    } else {
      yield put(createFieldSuccess(data));
    }
  } catch (error) {
    yield put(createFieldFailure(error));
  }
}

// UPDATE
const updateFieldApi = async ({ data, fieldId, accessToken }) => {
  try {
    const response = await axios.put(`${URL}${fieldId}/`, data, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleUpdateFieldSaga(action) {
  try {
    yield call(updateFieldApi, action.payload);
    const { accessToken, fieldId } = action.payload;
    const fieldDetailData = yield call(getFieldDetail, accessToken, fieldId);
    yield put(updateFieldSuccess(fieldDetailData));
    yield put(fetchFieldsStart({ accessToken }));
  } catch (error) {
    yield put(updateFieldFailure(error));
  }
}

// DELETE

const deleteFieldApi = async ({ fieldId, accessToken }) => {
  try {
    const response = await axios.delete(`${URL}${fieldId}/`, {
      headers: getAuthHeaders(accessToken),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: error.message };
  }
};

function* handleDeleteFieldSaga(action) {
  try {
    const data = yield call(deleteFieldApi, action.payload);
    yield put(deleteFieldSuccess(data));

    if (action.payload?.accessToken) {
      yield put(fetchFieldsStart({ accessToken: action.payload.accessToken }));
    }
  } catch (error) {
    yield put(deleteFieldFailure(error));
  }
}

export default function* fieldSaga() {
  yield takeLatest(createFieldStart.type, handleCreateFieldSaga);
  yield takeLatest(updateFieldStart.type, handleUpdateFieldSaga);
  yield takeLatest(deleteFieldStart.type, handleDeleteFieldSaga);
  yield takeLatest(fetchFieldDetailStart.type, handleGetFieldDetail);
}
